import {Injectable} from '@angular/core';
import {Actions, createEffect, CreateEffectMetadata, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {AppState} from '../store';
import {MetricsService} from '../../services/metrics.service';
import {Observable, switchMap, withLatestFrom} from 'rxjs';
import {TypedAction} from '@ngrx/store/src/models';
import {Serializer, SerializerResponse, Serializers} from '../../interfaces/serializer';
import {DataSetMetric} from '../../interfaces/metrics';
import {
  createDataSetMetric,
  createDataSetMetricSuccess,
  deleteDataSetMetric,
  deleteDataSetMetricSuccess,
  loadDataSetMetrics,
  loadDataSetMetricsSuccess,
  updateDataSetMetrics,
  updateDataSetMetricsSuccess,
  loadPublicDataSetMetrics,
  loadPublicDataSetMetricsSuccess,
  updateDataSetMetric,
  updateDataSetMetricSuccess, loadDataSetUsageMetrics
} from './dataSetMetrics.actions';
import {filter, map, tap} from 'rxjs/operators';
import {Site} from '../../interfaces/site';
import {selectSite} from '../init/init.selectors';
import {PublicReportsService} from '../../services/public-reports.service';
import {DatasetUsageComponentObject} from "../../classes/dataset-usage-component-object";

@Injectable()
export class DataSetMetricsEffects {
  private readonly _dataset$: Observable<Serializer<Site>> = this._store.select(selectSite);

  private readonly _load$: Observable<TypedAction<string> & { metrics: Serializers<DataSetMetric> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { metrics: Serializers<DataSetMetric> }> => this._actions$.pipe(
    ofType(loadDataSetMetrics),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetMetric>>> => this._metricsService.getDataSetMetrics(dataset)),
    map((response: SerializerResponse<Serializers<DataSetMetric>>): TypedAction<string> & { metrics: Serializers<DataSetMetric> } => loadDataSetMetricsSuccess({
      metrics: response.data
    }))
  ));

  private readonly _updates$: Observable<TypedAction<string> & { metrics: Serializers<DataSetMetric> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { metrics: Serializers<DataSetMetric> }> => this._actions$.pipe(
    ofType(updateDataSetMetrics),
    withLatestFrom(this._dataset$),
    switchMap(([_, dataset]: [TypedAction<string>, Serializer<Site>]): Observable<SerializerResponse<Serializers<DataSetMetric>>> => this._metricsService.getDataSetMetrics(dataset)),
    map((response: SerializerResponse<Serializers<DataSetMetric>>): TypedAction<string> & { metrics: Serializers<DataSetMetric> } => updateDataSetMetricsSuccess({
      metrics: response.data
    }))
  ));

  private readonly _loadPublic$: Observable<TypedAction<string> & { metrics: Serializers<DataSetMetric> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { metrics: Serializers<DataSetMetric> }> => this._actions$.pipe(
    ofType(loadPublicDataSetMetrics),
    switchMap((action: TypedAction<string> & { uuid: string, token: string }): Observable<SerializerResponse<Serializers<DataSetMetric>>> => this._publicReportsService.getDataSetMetrics(action.uuid, action.token)),
    map((response: SerializerResponse<Serializers<DataSetMetric>>): TypedAction<string> & { metrics: Serializers<DataSetMetric> } => loadPublicDataSetMetricsSuccess({
      metrics: response.data
    }))
  ));

  private readonly _create$: Observable<TypedAction<string> & { metric: Serializer<DataSetMetric> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { metric: Serializer<DataSetMetric> }> => this._actions$.pipe(
    ofType(createDataSetMetric),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string>  & { metric: Partial<DataSetMetric> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetMetric>>> => this._metricsService.createDataSetMetric(dataset, action.metric)),
    map((response: SerializerResponse<Serializer<DataSetMetric>>): TypedAction<string> & { metric: Serializer<DataSetMetric> } => createDataSetMetricSuccess({
      metric: response.data
    }))
  ));

  private readonly _update$: Observable<TypedAction<string> & { metric: Serializer<DataSetMetric> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { metric: Serializer<DataSetMetric> }> => this._actions$.pipe(
    ofType(updateDataSetMetric),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string>  & { metric: Serializer<DataSetMetric>, update: Partial<DataSetMetric> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetMetric>>> => this._metricsService.updateDataSetMetric(dataset, action.metric, action.update)),
    map((response: SerializerResponse<Serializer<DataSetMetric>>): TypedAction<string> & { metric: Serializer<DataSetMetric> } => updateDataSetMetricSuccess({
      metric: response.data
    }))
  ));

  private readonly _delete$: Observable<TypedAction<string> & { metric: Serializer<DataSetMetric> }> & CreateEffectMetadata = createEffect((): Observable<TypedAction<string> & { metric: Serializer<DataSetMetric> }> => this._actions$.pipe(
    ofType(deleteDataSetMetric),
    withLatestFrom(this._dataset$),
    switchMap(([action, dataset]: [TypedAction<string>  & { metric: Serializer<DataSetMetric> }, Serializer<Site>]): Observable<SerializerResponse<Serializer<DataSetMetric>>> => this._metricsService.deleteDataSetMetric(dataset, action.metric)),
    map((response: SerializerResponse<Serializer<DataSetMetric>>): TypedAction<string> & { metric: Serializer<DataSetMetric> } => deleteDataSetMetricSuccess({
      metric: response.data
    }))
  ));

  private readonly _loadUsageMetrics$: Observable<TypedAction<string>> & CreateEffectMetadata = createEffect(() => this._actions$.pipe(
    ofType(loadDataSetUsageMetrics),
    filter((action: TypedAction<string> & { dataSetUsage: DatasetUsageComponentObject }): boolean => !action.dataSetUsage.metricsLoaded$.value),
    tap((action: TypedAction<string> & { dataSetUsage: DatasetUsageComponentObject }) => action.dataSetUsage.metricsLoaded$.next(true)),
    map((): TypedAction<string> => loadDataSetMetrics())
  ));

  constructor(
    private readonly _actions$: Actions,
    private readonly _store: Store<AppState>,
    private readonly _metricsService: MetricsService,
    private readonly _publicReportsService: PublicReportsService
  ) {}

}
